import { AnalyticsEvent, AnalyticsManager } from '@oy/analytics';
import { Button, ButtonSize, Text } from '@oy/uidesktop';
import { LogoIcon } from '@oy/uiicon';
import classNames from 'classnames';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ProductActivationType } from 'src/constants';
import { useTranslate, useUpgradeAccountPage } from 'src/hooks';
import {
  ActionPageType,
  AmplitudeActionPage,
  DEFAULT_ACTIVE_PRODUCT,
  StatusProduct,
} from './constant';
import styles from './FormActivation.module.css';

type FormActivationLinkProps = {
  listStatusPaymentMethods?: StatusProduct[];
  size?: 'standard' | 'small';
  actionPage: ActionPageType;
};

type Icon = {
  icon: React.ReactNode;
  show: boolean;
};

const generateIcon = (product: ProductActivationType) => {
  switch (product) {
    case ProductActivationType.Shopee:
      return <LogoIcon name="shopeepay" />;
    case ProductActivationType.LinkAja:
      return <LogoIcon name="linkaja" className={styles.staticSizeLogo} />;
    case ProductActivationType.DANA:
      return <LogoIcon name="dana" />;
    case ProductActivationType.QRIS:
      return <LogoIcon name="qris" />;
    default:
      return <LogoIcon name="shopeepay" />;
  }
};

const generateEwalletName = (product: ProductActivationType) => {
  switch (product) {
    case ProductActivationType.Shopee:
      return 'ShopeePay';
    case ProductActivationType.LinkAja:
      return 'LinkAja';
    case ProductActivationType.DANA:
      return 'DANA';
    case ProductActivationType.QRIS:
      return 'QRIS';
    default:
      return '';
  }
};

const FormActivationLink = ({
  listStatusPaymentMethods,
  size,
  actionPage,
}: FormActivationLinkProps) => {
  const { getTranslation } = useTranslate();
  const navigate = useNavigate();

  const { openUpgradeAccountPage } = useUpgradeAccountPage();

  const isHaveAccessForm = actionPage !== ActionPageType.UpgradeAccount;

  const openLinkForm = () => {
    AnalyticsManager.instance().track(
      AnalyticsEvent.request_payment_checkout_method,
      {
        from: AmplitudeActionPage[actionPage],
      },
    );
    if (isHaveAccessForm) {
      navigate('/wallet-request');
      return;
    }

    AnalyticsManager.instance().track(
      AnalyticsEvent.click_upgrade_full_service,
    );
    openUpgradeAccountPage();
  };

  const generateListIcon = () => {
    const listIcon: Icon[] = [];
    const recordItem: StatusProduct[] =
      listStatusPaymentMethods || DEFAULT_ACTIVE_PRODUCT;
    recordItem.forEach(item => {
      listIcon.push({ icon: generateIcon(item.type), show: !item.enabled });
    });
    return listIcon;
  };

  const generateListEwalletName = () => {
    const recordItem: StatusProduct[] =
      listStatusPaymentMethods || DEFAULT_ACTIVE_PRODUCT;
    const listEwallet = recordItem
      .filter(item => !item.enabled)
      .map(item => generateEwalletName(item.type));
    return listEwallet;
  };

  return (
    <div
      className={classNames(styles.container, {
        [styles.small]: size === 'small',
      })}
      onClick={openLinkForm}
    >
      <div className={styles.ornament} />
      <div className={styles.content}>
        <div className={styles.listLogo}>
          {generateListIcon().map((el, index) => (
            <span key={index}>{el.show && el.icon}</span>
          ))}
        </div>
        {actionPage === ActionPageType.Ewallet ? (
          <Text textStyle={Text.Style.BodyMdRegular}>
            {getTranslation(
              'kyb_othersStep_productAndReferral_formActivation_ewalletLabel',
            )}{' '}
            {generateListEwalletName().join(', ')}
          </Text>
        ) : (
          <Text textStyle={Text.Style.BodyMdRegular}>
            {getTranslation(
              isHaveAccessForm
                ? 'kyb_othersStep_productAndReferral_formActivation_labelHaveAccessForm'
                : 'kyb_othersStep_productAndReferral_formActivation_labelNotHaveAccessForm',
            )}
          </Text>
        )}
      </div>
      <Button size={ButtonSize.Small}>
        {getTranslation(
          isHaveAccessForm
            ? 'kyb_othersStep_productAndReferral_formActivation_action'
            : 'kyb_othersStep_productAndReferral_formActivation_action',
        )}
      </Button>
    </div>
  );
};

FormActivationLink.ActionPageType = ActionPageType;

export default FormActivationLink;
